import {
  Circle,
  Document,
  Image,
  Link,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';
import dayjs from 'dayjs';
import signaturePath from '@/assets/images/signature-bd.png';
import type { CurrentUser } from '@/types';
import { registerFonts } from '@/utils/server/fonts';

// Register fonts
registerFonts();

// Base styles for common elements
const baseStyles = StyleSheet.create({
  text: {
    fontFamily: 'Inter',
    fontWeight: 400,
  },
  boldText: {
    fontFamily: 'Inter',
    fontWeight: 700,
  },
});

// Page layout styles
const layoutStyles = StyleSheet.create({
  page: {
    padding: '18mm 18mm',
    fontFamily: 'Inter',
    backgroundColor: '#ffffff',
    // DIN A4 dimensions: 210mm × 297mm
    width: '210mm',
    height: '297mm',
    position: 'relative',
  },
});

// Combined styles for all components
const styles = StyleSheet.create({
  // Header styles
  logo: {
    width: 104,
    height: 22,
    marginBottom: 32,
  },
  metaSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  meta: {
    ...baseStyles.boldText,
    fontSize: 9,
    color: '#000000',
    lineHeight: 1.4,
  },
  title: {
    ...baseStyles.boldText,
    fontSize: 20,
    marginBottom: 16,
    color: '#000000',
  },

  // Content styles
  container: {
    marginTop: 24,
  },
  paragraph: {
    ...baseStyles.text,
    fontSize: 11,
    marginBottom: 16,
    lineHeight: 1.4,
  },
  bulletPoints: {
    ...baseStyles.text,
    fontSize: 11,
    marginBottom: 16,
    lineHeight: 1.3,
    paddingLeft: 12,
  },

  // Footer styles
  footerContainer: {
    position: 'absolute',
    bottom: '18mm',
    left: '18mm',
    right: '18mm',
    borderTop: '1px solid #DDDDDD',
    paddingTop: 20,
  },
  signatureContainer: {
    marginTop: 20,
    width: '45mm',
  },
  signatureImage: {
    width: '32mm',
    height: '35mm',
  },
  signatureText: {
    marginTop: 8,
    ...baseStyles.text,
    fontSize: 11,
  },
  footerGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerBlock: {
    width: '23%',
  },
  footerTitle: {
    ...baseStyles.boldText,
    fontSize: 10,
    marginBottom: 4,
    color: '#000000',
  },
  footerText: {
    ...baseStyles.text,
    fontSize: 10,
    lineHeight: 1.4,
    color: '#666666',
  },
  link: {
    color: '#000000',
    textDecoration: 'underline',
  },
});

const Logo = () => (
  <Svg style={styles.logo} viewBox="0 0 67 14">
    <Circle cx="7.87936" cy="6.98825" r="6.98092" fill="#4D34FA" />
    <Path
      d="M7.38679 2.79968H8.328V4.58171C10.1427 4.71493 11.4374 5.7679 11.4374 7.58808V10.3655H10.4962V7.61837C10.4962 6.34224 9.72988 5.63617 8.328 5.49122V10.3554H7.38679V5.49122C5.98491 5.63617 5.21856 6.34224 5.21856 7.61837V10.3655H4.27734V7.58808C4.27734 5.7679 5.57213 4.71493 7.38679 4.58171V2.79968Z"
      fill="#F6F6F6"
    />
    <Path
      d="M19.3828 10.3262H21.2124V7.06764C21.2124 5.78558 21.76 5.17126 22.5479 5.17126C23.2691 5.17126 23.5896 5.77222 23.5896 6.76048V10.3262H25.4192V7.06764C25.4192 5.78558 25.9667 5.17126 26.7547 5.17126C27.4758 5.17126 27.7963 5.77222 27.7963 6.76048V10.3262H29.626V6.45332C29.626 4.65042 28.7178 3.50191 27.1286 3.50191C26.3674 3.50191 25.4726 3.90255 25.0586 4.73055C24.658 3.95597 23.9635 3.50191 22.9886 3.50191C22.174 3.50191 21.573 3.8892 21.2124 4.41003V3.64881H19.3828V10.3262Z"
      fill="black"
    />
    <Path
      d="M33.6448 13.1174L37.8382 3.64881H35.8216L34.0455 7.97577L32.2559 3.64881H30.2527L33.0572 9.95228L31.6549 13.1174H33.6448Z"
      fill="black"
    />
    <Path
      d="M43.8294 8.14938L46.7408 4.15629V10.3262H48.6772V0.844299H46.9144L43.8294 5.06442L40.7444 0.844299H38.9816V10.3262H40.9181V4.15629L43.8294 8.14938Z"
      fill="black"
    />
    <Path
      d="M53.9362 0.844299H50.7844V10.3262H52.7209V7.2279H53.9362C55.8593 7.2279 57.2615 5.839 57.2615 4.0361C57.2615 2.2332 55.8593 0.844299 53.9362 0.844299ZM53.9095 5.41164H52.7209V2.66055H53.9095C54.7909 2.66055 55.3785 3.26152 55.3785 4.0361C55.3785 4.81068 54.7909 5.41164 53.9095 5.41164Z"
      fill="black"
    />
    <Path
      d="M66.287 6.58687V0.844299H64.3505V6.61358C64.3505 7.86893 63.576 8.6168 62.4274 8.6168C61.2789 8.6168 60.5043 7.86893 60.5043 6.61358V0.844299H58.5679V6.58687C58.5679 8.95067 60.1571 10.4865 62.4274 10.4865C64.671 10.4865 66.287 8.95067 66.287 6.58687Z"
      fill="black"
    />
  </Svg>
);

export default function CertificatePDF({
  user,
  courseTitle,
  purchasedAt,
}: {
  user: CurrentUser;
  courseTitle: string;
  purchasedAt: Date;
}) {
  const today = dayjs().format('DD.MM.YYYY');
  const purchasedAtFormatted = dayjs(purchasedAt).format('DD.MM.YYYY');

  const {
    metaSection,
    meta,
    title,
    container,
    paragraph,
    bulletPoints,
    footerContainer,
    signatureContainer,
    signatureImage,
    signatureText,
    footerGrid,
    footerBlock,
    footerTitle,
    footerText,
    link,
  } = styles;

  return (
    <Document>
      <Page size="A4" style={layoutStyles.page}>
        <View>
          <Logo />
          <View style={metaSection}>
            <View>
              <Text style={meta}>
                Mymorize UG (haftungsbeschränkt){'\n'}
                Holzfeld 35, 83317 Teisendorf{'\n\n'}
              </Text>
              <Text style={paragraph}>
                {user.firstName} {user.lastName}
                {'\n'}
                {user.address?.street}
                {'\n'}
                {user.address?.zipCode} {user.address?.city}
              </Text>
            </View>
            <View>
              <Text style={meta}>Ausstellungsdatum{'\n'}</Text>
              <Text style={paragraph}>{today}</Text>
              <Text style={meta}>Ansprechpartner{'\n'}</Text>
              <Text style={paragraph}>Benedikt Dertinger</Text>
            </View>
          </View>
        </View>

        <View style={container}>
          <Text style={title}>Teilnahmebescheinigung</Text>
          <Text style={paragraph}>
            Hiermit wird{' '}
            <Text style={baseStyles.boldText}>
              {user.firstName} {user.lastName}
            </Text>{' '}
            (geboren am {dayjs(user.birthDate).format('DD.MM.YYYY')}) die Teilnahme am
            psychologischen Aufarbeitungskurs &bdquo;
            {courseTitle}&ldquo; im Kontext der Verkehrsteilnahme bestätigt.
          </Text>

          <Text style={paragraph}>Der Kurs bestand aus folgenden Inhalten:</Text>

          <Text style={bulletPoints}>
            - Psychoedukation bezüglich Zusammenhang mit Emotionen und Verkehrsteilnahme {'\n'}-
            Motivklärung mit Exploration aufrechterhaltender Faktoren {'\n'}- Explikation von kurz-
            und langfristigen Folgen des bisherigen Verhaltens {'\n'}- Möglichkeiten der
            Verhaltensänderung {'\n'}- sowie Möglichkeiten der Stabilisierung funktionaler
            Verhaltensmuster.
          </Text>

          <Text style={paragraph}>
            Bei diesem Kurs handelt es sich um eine von Psychologen erstellte digitale Anwendung.
            Der Erwerb erfolgte zum {purchasedAtFormatted} mit Zugriff für 6 Monate. Die digitale
            Anwendung wird in Eigenregie genutzt.
          </Text>
          <View style={signatureContainer}>
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <Image src={signaturePath.src} style={signatureImage} />
            <Text style={signatureText}>Benedikt Dertinger{'\n'}Psychologe, M.Sc.</Text>
          </View>
        </View>

        <View style={footerContainer}>
          <View style={footerGrid}>
            <View style={footerBlock}>
              <Text style={footerTitle}>Mymorize UG{'\n'}(haftungsbeschränkt)</Text>
              <Text style={footerText}>
                Holzfeld 35{'\n'}
                83317 Teisendorf
              </Text>
            </View>

            <View style={footerBlock}>
              <Text style={footerTitle}>Geschäftsführer</Text>
              <Text style={footerText}>
                Benedikt Dertinger, Fabian Krüger{'\n'}
                und Edgard Jung
              </Text>
            </View>

            <View style={footerBlock}>
              <Text style={footerTitle}>Amtsgericht</Text>
              <Text style={footerText}>Traunstein HRB 28469</Text>
              <Text style={[footerTitle, { marginTop: 8 }]}>Steuernummer</Text>
              <Text style={footerText}>163/132/90429</Text>
            </View>

            <View style={footerBlock}>
              <Text style={footerTitle}>Info & Kontakt</Text>
              <Text style={footerText}>
                <Link src="https://mympu.com" style={link}>
                  mympu.com
                </Link>
              </Text>
              <Text style={footerText}>
                <Link src="mailto:info@mympu.com" style={link}>
                  info@mympu.com
                </Link>
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
